import type { ApolloClient } from "@apollo/client";
import { VisitorProfileDocument } from "@/lib/graphql/queries/generated/profile.generated";
import { isSupportedLocale } from "@/lib/locales";
import { resetRuntimeLocale } from "@/lib/runtime-locale";

/** The two outbox ops that write the visitor's profile / preferences. */
export function isProfileOp(op: string): boolean {
  return op === "setProfile" || op === "setPreferences";
}

/**
 * A queued profile / preference write was permanently rejected by the server (validation, a locked
 * field, a locked theme). The UI had already applied it optimistically to the Apollo cache, so
 * drop the cached profile: active watchers and the next mount fetch the server's value, which puts
 * the form, the theme buttons and the chrome back to what is really saved.
 */
export function discardOptimisticProfile(client: ApolloClient): void {
  client.cache.evict({ id: "ROOT_QUERY", fieldName: "visitorProfile" });
  client.cache.gc();
}

/**
 * The language lives outside Apollo (the runtime-locale bus), so evicting the cache does not undo
 * a rejected language pick. Read what the server really holds and put the chrome back to it (or
 * to the host's locale when none is saved). If the read fails the chrome is left as it is.
 */
export async function restoreServerLanguage(client: ApolloClient): Promise<void> {
  try {
    const { data } = await client.query({
      query: VisitorProfileDocument,
      fetchPolicy: "network-only",
    });
    const language = data?.visitorProfile?.preferences?.language;
    resetRuntimeLocale(isSupportedLocale(language) ? language : null);
  } catch {
    // Offline or a failed read: keep the current chrome; the next profile fetch re-applies the saved language.
  }
}
